import { useCanViewEmails } from '../../../helpers/useCanViewEmails';
import React, { createRef, useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'material-ui-snackbar-provider';
import { Tooltip } from '@mui/material';
import { useHistory } from 'react-router-dom';
import NotReadyIcon from '@mui/icons-material/DoNotDisturbAlt';
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';
import cx from 'classnames';

import { FOLDER_CONTENT_FILTER, NOTIFICATION, VIDEOFEED_STATUS, VIDEOFEED_TYPE, VIDEOPAGE_TYPE } from '../../../enums/ENUMS';
import { ReactComponent as ComposerIcon } from '../../../assets/icons/composer.svg';
import { ReactComponent as MovieIcon } from '../../../assets/icons/movieCollection.svg';
import { postVideofeedViewRequest, updateVideofeedTitle } from '../../../api';
import useOnClickOutside from '../../../helpers/useOnClickOutside';
import { DateFormatter } from '../../../helpers/dateFormatter';
import StorageIcon from '../../../assets/icons/storage.svg';
import DeleteVideofeedModal from '../DeleteVideofeedModal';
import {
  refetchFeeds,
  updateFeedToMove,
  updateFeedsToMove,
  updateFeedToShare,
  updateShowMoveFeedModal,
  updateShowShareModal,
} from '../../../store/videoFeedsList/videoFeedsListSlice';
import { MoreIcon } from '../../../assets/icons/threedots';
import { bytesToGB } from '../../../helpers/bytesToGB';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import TrashButton from '../../modules/TrashButton';
import ShareButton from '../../modules/ShareButton';
import { bytesToMB, findSearchValue, secondsToHours, useDimensions } from '../../../helpers';
import EditButton from '../../modules/EditButton';
import { VIDEOFEED } from '../../../enums/TYPES';
import { PATHS } from '../../../enums/PATHS';
import styles from './Videofeed.module.scss';
import Icon from './icon';
import NotReady from '../../../assets/icons/not-ready';
import { updatePkgToDelete } from '../../../store/sharedStore/sharedStoreSlice';
import DownloadButton from '../../modules/DownloadButton';
import DownloadDataModal from '../../modules/VideoFileList/DownloadDataModal';
import { convertSeconds } from '../../Timeline/utils';
import { SortOptions } from '../../../enums/SORT';
import FileTypeThumbnail from '../../modules/FileTypeThumbnail';
import { getDateColumnTooltipLabel, getDateColumnTooltipValue, getDateColumnValue } from '../../modules/DateSortHeader';
import ReplaceThumbnailModal from '../../modules/ReplaceThumbnailModal';
import { removeVideofeedThumbnail, uploadVideofeedThumbnail } from '../../../api/videofeeds';
import ThumbnailButton from '../../modules/ThumbnailButton';

type TVideofeed = {
  videofeed: VIDEOFEED;
  fetchAll?: () => void;
  feedIndex?: number;
  loading?: boolean;
  orgType?: 'parent' | 'child';
  dateSortOption?: SortOptions | null;
};

const CollaborativeVideofeed: React.FC<TVideofeed> = ({ videofeed, fetchAll, feedIndex = 0, loading, orgType, dateSortOption }) => {
  const canViewEmails = useCanViewEmails();
  const normalizedReferenceTimelines = Array.isArray(
    (videofeed as any)?.reference_timelines,
  )
    ? ((videofeed as any).reference_timelines as VIDEOFEED[])
    : [];
  const uniqueCollectionTimelineIds = new Set(
    normalizedReferenceTimelines
      .map((timeline) => String(timeline?.id || ''))
      .filter(Boolean),
  );
  if (
    uniqueCollectionTimelineIds.size === 0 &&
    Boolean((videofeed as any)?.is_collection_root)
  ) {
    uniqueCollectionTimelineIds.add(String(videofeed.id));
  }
  const collectionTimelineCount = uniqueCollectionTimelineIds.size;
  const isCollectionRow =
    Boolean((videofeed as any)?.is_collection_root) ||
    collectionTimelineCount > 0 ||
    Boolean(videofeed?.has_reference_timelines);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteCollectionMode, setDeleteCollectionMode] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showThumbnailModal, setShowThumbnailModal] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState(videofeed.thumbnail_url || '');
  const [title, setTitle] = useState(videofeed.title);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [isEditing, setIsEditing] = useState(false);
  const [hover, setHover] = useState(false);
  const history = useHistory();
  const notification = useSnackbar();
  const dispatch = useAppDispatch();
  const { myOrganization } = useAppSelector((state) => state.user);
  const folderId = findSearchValue('folderId');
  const folderTrail = findSearchValue('folderTrail');
  const contentFilter = findSearchValue('contentFilter');
  const previewUrl = thumbnailUrl || videofeed?.videos?.[0]?.s3_preview_url || '';
  const organizationHaveDownloadHours = Number(myOrganization?.download_hours_limit || 0) > 0;
  const canShowMyComposerDownload = videofeed.type !== VIDEOFEED_TYPE.MY_COMPOSER || organizationHaveDownloadHours;
  const canReplaceThumbnail =
    videofeed.type === VIDEOFEED_TYPE.COMPOSER && videofeed.status === VIDEOFEED_STATUS.DONE;

  useEffect(() => {
    setThumbnailUrl(videofeed.thumbnail_url || '');
  }, [videofeed.thumbnail_url]);
  const shellCollectionTarget =
    isCollectionRow &&
    (videofeed?.topics?.length || 0) === 0 &&
    normalizedReferenceTimelines.length > 0
      ? normalizedReferenceTimelines[0]
      : null;

  const handleLocalStorageKeysForResumeUploading = () => {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        if (
          key.startsWith('=videofeedToResume=') &&
          localStorage &&
          localStorage.getItem(key) &&
          localStorage.getItem(key)?.includes(videofeed.id)
        ) {
          const pkg = JSON.parse(localStorage.getItem(key)!);
          if (pkg && pkg?.s3_filename === videofeed?.s3_filename) {
            localStorage.removeItem(key);

            dispatch(updatePkgToDelete(videofeed));
          }
        }
      }
    }
  };

  const onReplaceThumbnail = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDotsMenu(false);
    setShowThumbnailModal(true);
  };

  useEffect(() => {
    if (videofeed?.status === VIDEOFEED_STATUS.TRANSCODING || videofeed?.status === VIDEOFEED_STATUS.DONE) {
      handleLocalStorageKeysForResumeUploading();
    }
  }, [videofeed]);

  const onHover = () => {
    setHover(!hover);
  };

  const handleDeleteClick = (e: any, deleteCollection = false) => {
    e.preventDefault();
    e.stopPropagation();
    setDeleteCollectionMode(deleteCollection);
    setShowDeleteModal(true);
  };

  const onDeleteModalClose = () => {
    setShowDeleteModal(false);
    setDeleteCollectionMode(false);
  };
  type CustomEventHandler = React.MouseEvent<HTMLDivElement> | React.MouseEvent<SVGSVGElement>;
  const redirectToVideoPage = (e: CustomEventHandler) => {
    if (isEditing) return;
    if (loading) return;
    const targetFeed = shellCollectionTarget || videofeed;
    const targetFeedIndex = feedIndex;
    if (targetFeed?.status && targetFeed.status !== VIDEOFEED_STATUS.DONE) {
      return;
    }
    const parentPage = findSearchValue('parentPage');

    if (e.currentTarget.contains(e.target as Node) || (e.target as HTMLElement).tagName === 'path') {
      postVideofeedViewRequest(targetFeed.id);
      switch (targetFeed.type) {
        case VIDEOFEED_TYPE.COMPOSER:
          history.push(
            `${PATHS.COMPOSER_PAGE.path}?feedId=${targetFeed.id}&parentFolders=true&folderId=${folderId}&fileside=true&feed=${targetFeedIndex}&clipId=${targetFeed.videos[0].id}&clip=0&parentPage=${parentPage}&pageType=${VIDEOPAGE_TYPE.COMPOSER}${
              folderTrail ? `&folderTrail=${folderTrail}` : ''
            }${
              contentFilter && contentFilter !== FOLDER_CONTENT_FILTER.ALL
                ? `&contentFilter=${contentFilter}`
                : ''
            }`,
          );
          break;
        case VIDEOFEED_TYPE.MY_COMPOSER:
          history.push(
            `${PATHS.COMPOSER_PAGE.path}?feedId=${targetFeed.id}&parentFolders=true&fileside=true&feed=${targetFeedIndex}&clipId=${targetFeed.videos[0].id}&clip=0&parentPage=${parentPage}&pageType=${VIDEOFEED_TYPE.MY_COMPOSER}${
              folderTrail ? `&folderTrail=${folderTrail}` : ''
            }${
              contentFilter && contentFilter !== FOLDER_CONTENT_FILTER.ALL
                ? `&contentFilter=${contentFilter}`
                : ''
            }`,
          );
          break;
        default:
          history.push(
            `${PATHS.TIMELINE.path}?feedId=${targetFeed.id}&parentFolders=true&folderId=${folderId}&fileside=true&feed=${targetFeedIndex}&clipId=${targetFeed.videos[0].id}&clip=0&parentPage=${parentPage}&pageType=${VIDEOPAGE_TYPE.TIMELINE}${
              folderTrail ? `&folderTrail=${folderTrail}` : ''
            }${
              contentFilter && contentFilter !== FOLDER_CONTENT_FILTER.ALL
                ? `&contentFilter=${contentFilter}`
                : ''
            }`,
          );
      }
    }
  };

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  };

  const inputRef = useRef<HTMLInputElement | null>(null);
  const enableFeedNameEdit = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    if (showDotsMenu) {
      setShowDotsMenu(false);
    }
    setIsEditing(true);
    setHover(true);
    setTimeout(() => {
      if (inputRef && inputRef?.current) {
        inputRef.current.focus();
      }
    }, 150);
  };
  const handleEdit = async (e: any) => {
    e.preventDefault();
    if (title.trim().length === 0) {
      setTitle(videofeed.title);
      setTimeout(() => {
        notification.showMessage(NOTIFICATION.EMPTY);
      }, 100);
      return;
    }

    setIsEditing(!isEditing);
    await updateVideofeedTitle(videofeed.id, title);
    setHover(false);

    dispatch(refetchFeeds());
  };

  const actionMenuRef = useRef<HTMLDivElement | null>(null);
  const [showDotsMenu, setShowDotsMenu] = useState(false);

  const switchDotsMenu = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (showDotsMenu) {
      setShowDotsMenu(false);
      return;
    }

    const folderElement = e.currentTarget.closest(`.${styles.container}`) as HTMLElement;
    if (!folderElement) return;

    const folderRect = folderElement.getBoundingClientRect();
    const menuWidth = actionMenuRef.current?.offsetWidth || 310;
    const menuHeight = actionMenuRef.current?.offsetHeight;

    let posX = e.clientX - folderRect.left;
    let posY = e.clientY - folderRect.top;

    // Ensure menu stays within folder boundaries
    // @ts-ignore
    if (posX + menuWidth > folderRect.width) {
      // @ts-ignore
      posX = folderRect.width - menuWidth - 10;
    }

    // Adjust if the menu would go below the viewport
    // @ts-ignore
    if (folderRect.bottom + menuHeight > window.innerHeight) {
      // @ts-ignore
      posY -= menuHeight - 80; // Move it upwards
    }

    setMenuPosition({
      x: folderRect.left + posX + 20,
      y: folderRect.top + posY,
    });

    setShowDotsMenu(true);
  };

  const switchDownloadDataModal = () => {
    setShowDownloadModal(!showDownloadModal);
  };

  const onDownload = (e: any) => {
    e.stopPropagation();
    setShowDownloadModal(true);
  };

  useOnClickOutside(actionMenuRef, (e) => {
    e.stopPropagation();
    setShowDotsMenu(false);
  });

  const onMoveClick = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(updateFeedToMove(videofeed));
    dispatch(updateFeedsToMove([videofeed]));
    dispatch(updateShowMoveFeedModal(true));
    setShowDotsMenu(false);
  };

  const onShareClick = async (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    const isCollectionShareTarget = Boolean((videofeed as any)?.is_collection_root);
    dispatch(
      updateFeedToShare({
        ...(videofeed as any),
        share_scope: isCollectionShareTarget ? 'collection' : 'single',
      }),
    );
    dispatch(updateShowShareModal(true));
    setShowDotsMenu(false);
  };

  const parentRef = createRef<HTMLDivElement>();
  const updateScrollPosition = () => {
    if (parentRef && parentRef?.current) {
      parentRef.current.scrollIntoView({
        behavior: 'smooth',
      });
    }
  };

  const { widthD } = useDimensions();

  return (
    <div
      ref={parentRef}
      className={cx(styles.container, showDotsMenu && styles.selectedVideofeed)}
      onClick={redirectToVideoPage}
      onContextMenu={switchDotsMenu}
    >
      <div className={styles.link} onClick={redirectToVideoPage}>
        <div className={styles.leftSide} onClick={redirectToVideoPage}>
          <FileTypeThumbnail
            className={styles.fileTypeThumbnail}
            icon={
              <Icon onClick={redirectToVideoPage}>
                {videofeed.status !== VIDEOFEED_STATUS.TRANSCODING && videofeed.status !== VIDEOFEED_STATUS.DONE ? (
                  <Tooltip title={'Video is not ready'}>
                    <div>
                      <NotReadyIcon className={styles.notReadyIcon} />
                    </div>
                  </Tooltip>
                ) : videofeed?.type === VIDEOFEED_TYPE.MY_COMPOSER || videofeed?.type === VIDEOFEED_TYPE.COMPOSER ? (
                  <ComposerIcon className={styles.contentTypeIcon} style={{ pointerEvents: 'none' }} />
                ) : (
                  <MovieIcon className={styles.contentTypeIcon} style={{ pointerEvents: 'none' }} />
                )}
              </Icon>
            }
          >
            {videofeed.status === VIDEOFEED_STATUS.DONE && previewUrl ? (
              <img alt={'preview url'} className={styles.thumbnail} onClick={redirectToVideoPage} src={previewUrl} />
            ) : (
              <div className={styles.imgContainer}>
                <NotReady className={styles.thumbnail} onClick={redirectToVideoPage} />
                {videofeed.status === VIDEOFEED_STATUS.TRANSCODING && (
                  <Tooltip title="Processing">
                    <div className={styles.progress}>
                      <LinearProgress
                        sx={{
                          width: '100%',
                          [`&.${linearProgressClasses.colorPrimary}`]: {
                            backgroundColor: '#F5F5F5',
                          },
                          [`& .${linearProgressClasses.bar}`]: {
                            backgroundColor: '#6500AA',
                          },
                        }}
                      />
                    </div>
                  </Tooltip>
                )}
              </div>
            )}
          </FileTypeThumbnail>
          <div />
          {isEditing ? (
            <form
              onClick={(e) => {
                e.stopPropagation();
              }}
              onSubmit={handleEdit}
            >
              <input ref={inputRef} className={styles.input} onChange={handleChange} defaultValue={videofeed.title} onBlur={handleEdit} />
            </form>
          ) : (
            <div
              className={cx(
                styles.name,
                videofeed.status !== VIDEOFEED_STATUS.TRANSCODING && videofeed.status !== VIDEOFEED_STATUS.DONE && styles.redTitle,
              )}
            >
              {title}
            </div>
          )}
        </div>
      </div>
      <div className={styles.rightSide}>
        <div className={styles.team}>{videofeed?.origin_org_name || videofeed.created_by.username || (canViewEmails ? videofeed.created_by.email : 'User')}</div>
        <div className={styles.status}>
          {videofeed.status === VIDEOFEED_STATUS.TRANSCODING && <div className={styles.statusBlue}>Processing</div>}
          {videofeed.status === VIDEOFEED_STATUS.DONE && <div className={styles.statusGreen}>Uploaded</div>}
          {videofeed.status !== VIDEOFEED_STATUS.TRANSCODING && videofeed.status !== VIDEOFEED_STATUS.DONE && (
            <div className={styles.statusRed}>Draft</div>
          )}
        </div>
        <Tooltip title={`${getDateColumnTooltipLabel(dateSortOption)}: ${DateFormatter(getDateColumnTooltipValue(videofeed, dateSortOption), true)}`}>
          <div className={styles.date}>
            {widthD < 1185
              ? DateFormatter(getDateColumnValue(videofeed, dateSortOption))
              : DateFormatter(getDateColumnValue(videofeed, dateSortOption), true)}
          </div>
        </Tooltip>
        <div className={styles.size}>
          {videofeed?.size && (
            <>
              <img src={StorageIcon} alt="storage" />
              {bytesToGB(+videofeed?.size, 2) > 1 ? `${bytesToGB(+videofeed?.size, 2)} GB` : `${bytesToMB(+videofeed?.size, 0)} MB`}
            </>
          )}
        </div>

        <div className={styles.duration} style={{ fontSize: videofeed?.duplicate ? '10px' : '12px' }}>
          {videofeed?.duration && (
            <Tooltip title={convertSeconds(videofeed.duration, false, true)}>
              <div>{videofeed?.duplicate ? `REF ${secondsToHours(videofeed.duration)}` : secondsToHours(videofeed.duration)}</div>
            </Tooltip>
          )}
        </div>

        <div className={styles.dotsMenuBtn}>
          <div className={styles.dotIcon} onClick={switchDotsMenu}>
            <MoreIcon />
          </div>
        </div>
      </div>
      {showDeleteModal && (
        <DeleteVideofeedModal
          clearLocalStorageData={handleLocalStorageKeysForResumeUploading}
          switchModal={onDeleteModalClose}
          open={showDeleteModal}
          videofeedId={videofeed.id}
          hasReferenceTimelines={deleteCollectionMode}
          deleteCollection={deleteCollectionMode}
          fetchAll={fetchAll}
        />
      )}
      <div
        ref={actionMenuRef}
        className={styles.actionMenu}
        style={{
          display: showDotsMenu ? 'block' : 'none',
          position: 'absolute',
          top: `${menuPosition.y}px`,
          left: `${menuPosition.x}px`,
        }}
      >
        {videofeed.status === VIDEOFEED_STATUS.DONE && (
          <>
            <div className={styles.menuOption} onClick={enableFeedNameEdit}>
              <EditButton /> Rename
            </div>
            {/* <div className={styles.menuOption} onClick={onMoveClick}>
                <MoveButton /> Move to
              </div> */}
          </>
        )}
        {orgType === 'child' && (
          <div className={styles.menuOption} onClick={onShareClick}>
            <ShareButton /> Share
          </div>
        )}
        {canReplaceThumbnail && (
          <div className={styles.menuOption} onClick={onReplaceThumbnail}>
            <ThumbnailButton /> Replace Thumbnail Image
          </div>
        )}
        {videofeed.status === VIDEOFEED_STATUS.DONE && canShowMyComposerDownload && (
          <div className={styles.menuOption} onClick={onDownload}>
            <DownloadButton /> Download
          </div>
        )}
        {isCollectionRow ? (
          <div className={styles.menuOption} onClick={(e) => handleDeleteClick(e, true)}>
            <TrashButton /> Delete collection
          </div>
        ) : (
          <div className={styles.menuOption} onClick={(e) => handleDeleteClick(e, false)}>
            <TrashButton /> Delete
          </div>
        )}
      </div>
      <ReplaceThumbnailModal
        open={showThumbnailModal}
        setOpen={setShowThumbnailModal}
        onUpload={(data) => uploadVideofeedThumbnail(videofeed.id, data)}
        onUploaded={(updatedFeed) => setThumbnailUrl(updatedFeed.thumbnail_url || '')}
        onRestore={thumbnailUrl ? () => removeVideofeedThumbnail(videofeed.id) : undefined}
        preview={{ type: 'file', title, imageUrl: previewUrl }}
      />
      <DownloadDataModal switchModal={switchDownloadDataModal} isOpen={showDownloadModal} feedId={videofeed.id} />
    </div>
  );
};

export default CollaborativeVideofeed;
